/* eslint-disable @typescript-eslint/no-explicit-any */
import { Autocomplete, CircularProgress, Paper, TextField, Theme } from '@mui/material';
import { BaseSyntheticEvent, SyntheticEvent, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';
import { FilterTypes, PaginationQueryParams, SelectOption } from '../../types';

const useStyles = makeStyles<{
  maxWidth?: string;
  autocompleteValue?: string;
  disabled?: boolean;
  minWidth?: string;
  width?: string;
  size?: 'small' | 'medium';
}>()((theme: Theme, { maxWidth, autocompleteValue, disabled, minWidth, width, size }) => ({
  root: {
    ...(maxWidth && { maxWidth }),
    ...(minWidth && { minWidth }),
    ...(width && { width }),

    margin: 0,
    justifyContent: 'flex-end',
    background: disabled ? 'rgba(255, 255, 255, 0.04)' : 'rgba(255, 255, 255, 0.03)',
    borderRadius: size === 'small' ? '8px' : '12px',

    '@media (max-width: 550px)': {
      width: '100%',
    },

    '&:hover': {
      '& .MuiAutocomplete-clearIndicator': {
        color: theme.palette.common.white,

        '&.Mui-disabled': {
          color: theme.palette.text.disabled,
        },
      },
    },

    '& .MuiOutlinedInput-input': {
      color: theme.palette.common.white,
      padding: size === 'small' ? '8.5px 14px !important' : '12px 16px !important',
      borderRadius: size === 'small' ? '8px' : '12px',

      '&.Mui-disabled': {
        color: theme.palette.text.disabled,
        WebkitTextFillColor: theme.palette.text.disabled,
      },
    },

    '& .MuiOutlinedInput-root': {
      color: theme.palette.text.disabled,
      borderRadius: size === 'small' ? '8px' : '12px',
      padding: 0,

      '&.Mui-focused': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderWidth: '2px',
          borderColor: theme.palette.info.main,
        },
      },

      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.secondary.dark,
        borderRadius: size === 'small' ? '8px' : '12px',

        '&.Mui-disabled': {
          borderColor: theme.palette.secondary.dark,
          borderRadius: size === 'small' ? '8px' : '12px',
        },
      },

      '&:hover': {
        border: 'none',

        '&.Mui-disabled': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: 0,
            borderColor: 'none',
          },
        },

        '& .MuiOutlinedInput-notchedOutline': {
          borderWidth: '2px',
          borderColor: theme.palette.info.main,
        },
      },

      // After :hover so the red error border isn't replaced by the hover colour
      '&.Mui-error .MuiOutlinedInput-notchedOutline': {
        borderWidth: '2px',
        borderColor: theme.palette.error.main,
      },

      '&.Mui-disabled': {
        color: theme.palette.text.disabled,
      },
    },

    '& .MuiInputLabel-root[data-shrink="false"]': {
      color: theme.palette.text.disabled,
      top: '-3px',

      '&.Mui-disabled': {
        color: theme.palette.text.disabled,
      },
    },

    '& .MuiInputLabel-root[data-shrink="true"]': {
      color: theme.palette.text.disabled,

      '&.Mui-focused': {
        color: theme.palette.common.white,
      },

      '&.Mui-disabled': {
        color: theme.palette.text.disabled,
      },
    },

    // After the label shrink rules so the error colour wins
    '& .MuiInputLabel-root.Mui-error': {
      color: theme.palette.error.main,
    },

    '& .MuiFormHelperText-root': {
      fontFamily: 'Inter',
      fontSize: '12px',
      lineHeight: '18px',
      marginTop: '6px',
      marginLeft: 0,
    },

    '& .MuiCircularProgress-root': {
      color: theme.palette.common.white,
    },

    '& .MuiAutocomplete-clearIndicator': {
      color: theme.palette.common.white,

      '&:hover': {
        color: theme.palette.common.white,
      },

      '&.Mui-disabled': {
        color: theme.palette.text.disabled,
      },
    },

    '& .MuiAutocomplete-popupIndicator': {
      color: autocompleteValue ? theme.palette.common.white : theme.palette.text.disabled,

      '&.Mui-disabled': {
        color: theme.palette.text.disabled,
      },
    },
  },
  paperDropdown: {
    background: theme.palette.secondary.dark,

    '& .MuiAutocomplete-noOptions': {
      fontSize: '14px',
      fontWeight: 500,
      fontFamily: 'Inter',
    },

    '& .MuiAutocomplete-loading': {
      fontSize: '14px',
      fontWeight: 500,
      fontFamily: 'Inter',
    },

    '& .MuiAutocomplete-option': {
      fontSize: '14px',
      fontWeight: 500,
      fontFamily: 'Inter',
    },
  },
}));

interface AddressAutocompleteProps {
  name: string;
  label: string;
  size?: 'small' | 'medium';
  textFieldClassName?: string;
  readOnly?: boolean;
  disabled?: boolean;
  maxWidth?: string;
  filters?: FilterTypes;
  optionsQuery: ({ page, size, count, match, filters }: PaginationQueryParams) => Promise<any>;
  mapQueryResults: (array: any[]) => any;
  isOptionEqualToValue?: (option: any, value: any) => boolean;
  onValueChange?: (item: any) => void;
  onSearchResultChange?: (array: any[]) => void;
  minWidth?: string;
  width?: string;
  isDisableClearable?: boolean;
  InputProps?: any;
  addressType?: string;
  // Optional error state, for callers that validate outside react-hook-form
  error?: boolean;
  helperText?: string;
}

const AddressAutocomplete = ({
  name,
  label,
  size,
  textFieldClassName,
  disabled,
  maxWidth,
  readOnly,
  filters,
  optionsQuery,
  mapQueryResults,
  isOptionEqualToValue,
  onValueChange,
  onSearchResultChange,
  minWidth,
  width,
  isDisableClearable,
  InputProps,
  error,
  helperText,
}: AddressAutocompleteProps) => {
  const { control, watch } = useFormContext();
  const autocompleteValue = watch(name);

  const styles = useStyles({ maxWidth, autocompleteValue, disabled, minWidth, width, size });

  const [page, setPage] = useState(1);
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const [optionsData, setOptionsData] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [totalCount, setTotalCount] = useState();
  const controlledInputValue = typeof InputProps?.value === 'string' ? InputProps.value : undefined;

  useEffect(() => {
    if (controlledInputValue !== undefined) {
      setInputValue(controlledInputValue);
    }
  }, [controlledInputValue]);

  const handleChangeValue = (value: string) => {
    setInputValue(value);

    // Keep parent state in sync for manual typing regardless of suggestion threshold.
    const currentFormValue = autocompleteValue;
    const currentDisplayValue = currentFormValue?.label || currentFormValue?.description || '';
    const shouldSyncClearedValue = value === '';

    if (
      !(currentFormValue?.placeId && value === currentDisplayValue) &&
      (value !== currentDisplayValue || shouldSyncClearedValue)
    ) {
      const customOption = { id: 'typing', label: value, description: value };
      onValueChange && onValueChange(customOption);
    }

    if (value.length < 6) {
      setOptionsData([]);
      setPage(1);
      setIsDataLoading(false);
    } else {
      setIsDataLoading(true);
      optionsQuery({ page, size: 10, count: true, match: value, filters })
        .then((res) => {
          if (res.data && res.data.resultSet) {
            setTotalCount(res.data.totalCount);
            onSearchResultChange?.(res.data.resultSet);
            const mappedData = mapQueryResults(res.data.resultSet);
            setOptionsData(mappedData || []);
          }
          setIsDataLoading(false);
        })
        .catch(() => {
          setOptionsData([]);
          setIsDataLoading(false);
        });
    }
  };

  const loadMoreResults = async () => {
    if (totalCount !== optionsData.length) {
      setIsDataLoading(true);
      optionsQuery({
        page: page + 1,
        size: 10,
        count: false,
        match: inputValue,
        filters,
      })
        .then((res) => {
          if (res.data) {
            setPage(page + 1);
            const mappedData = mapQueryResults(res.data.resultSet);
            setOptionsData([...optionsData, ...(mappedData as [])]);
            setIsDataLoading(false);
          }
        })
        .catch(() => {
          setIsDataLoading(false);
        });
    }
  };

  const handleScroll = (event: BaseSyntheticEvent) => {
    const listboxNode = event.currentTarget;

    const position = listboxNode.scrollTop + listboxNode.clientHeight;
    if (listboxNode.scrollHeight - position <= 1) {
      loadMoreResults();
    }
  };

  const handleBlur = () => {
    // Don't clear options immediately - let component handle it naturally
    // This prevents dropdown from closing before user can click an option
    setPage(1);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value } }) => (
        <Autocomplete
          noOptionsText="Start typing address line 1..."
          loading={isDataLoading}
          value={value || null}
          inputValue={controlledInputValue ?? inputValue}
          className={styles.classes.root}
          disabled={disabled}
          freeSolo={true}
          onInputChange={(_event, nextInputValue, reason) => {
            // The clear indicator can fire without a value change when freeSolo is used.
            // Explicitly sync clearing so controlled parents and RHF stay in sync.
            if (reason === 'clear') {
              setInputValue('');
              onChange(null);
              onValueChange?.(null);
            }

            if (reason === 'reset') {
              setInputValue(nextInputValue || '');
            }
          }}
          onChange={(_: SyntheticEvent, newValue: string | SelectOption | null) => {
            if (typeof newValue === 'string') {
              // Handle freeSolo string input
              const customOption = { id: 'custom', label: newValue };
              onChange(customOption);
              onValueChange && onValueChange(customOption);
            } else {
              if (newValue === null || (typeof newValue === 'object' && newValue.id === '' && newValue.label === '')) {
                onChange(null);
                onValueChange && onValueChange(null);
              } else {
                onChange(newValue);
                onValueChange && onValueChange(newValue);
              }
            }
          }}
          onClose={() => {
            // Clear options when dropdown closes (user clicks away or selects)
            setPage(1);
            setOptionsData([]);
          }}
          disableClearable={isDisableClearable}
          onBlur={handleBlur}
          getOptionLabel={(option: string | SelectOption) => {
            // Handle both string and object values for freeSolo
            if (typeof option === 'string') {
              return option;
            }
            if (!option) return '';
            return (option as any).label || (option as any).description || '';
          }}
          isOptionEqualToValue={isOptionEqualToValue}
          filterOptions={(options) => {
            // Don't filter at all - show all results from the API
            // The API already filters based on the search query, so client-side filtering is unnecessary
            // and can hide valid results when pasting full addresses
            return options;
          }}
          options={optionsData}
          readOnly={readOnly}
          PaperComponent={({ children }) => (
            <Paper className={styles.classes.paperDropdown}>{children}</Paper>
          )}
          ListboxProps={{
            role: 'list-box',
            onScroll: handleScroll,
            style: { maxHeight: '200px' },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              className={textFieldClassName}
              size={size}
              label={label}
              error={error}
              helperText={helperText}
              onChange={(e) => handleChangeValue(e.target.value)}
              // onBlur={() => {
              //   // DO NOTHING on blur - let the autocomplete component handle everything
              //   // The onBlur was causing issues by creating custom options when clicking away
              // }}
              InputProps={{
                ...params.InputProps,
                ...InputProps,
                endAdornment: (
                  <>
                    {isDataLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                    {InputProps?.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}
    />
  );
};

export default AddressAutocomplete;